<template>
  <div class="g-page">
    <div class="content-container">

      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">{{ $t("To'lov turlari") }}</h2>
        <div class="header-actions">
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input
              v-model="filter"
              type="search"
              :placeholder="$t('Qidiruv...')"
              class="modern-search"
            >
          </div>
          <button class="refresh-btn" @click="Load()" :title="$t('Yangilash')">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
          <button class="add-btn" @click="OpenCreate()">
            <b-icon icon="plus-circle-fill"></b-icon> {{ $t("Qo'shish") }}
          </button>
        </div>
      </div>

      <!-- JADVAL -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width: 60px">#</th>
                <th>{{ $t("Nomi") }}</th>
                <th style="width: 220px; text-align: center;">{{ $t("Amallar") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredRows" :key="item.id" @dblclick="OpenEdit(item)">
                <td><span class="id-badge">#{{ index + 1 }}</span></td>
                <td class="font-bold">
                  {{ item.name }}
                  <span v-if="isProtected(item.id)" class="protected-badge">{{ $t("Tizim") }}</span>
                </td>
                <td>
                  <div class="actions">
                    <button
                      class="icon-btn move"
                      :disabled="!!filter || itemPos(item) === 0"
                      :title="$t('Yuqoriga')"
                      @click="move(item, -1)"
                    >
                      <b-icon icon="arrow-up"></b-icon>
                    </button>
                    <button
                      class="icon-btn move"
                      :disabled="!!filter || itemPos(item) === items.length - 1"
                      :title="$t('Pastga')"
                      @click="move(item, 1)"
                    >
                      <b-icon icon="arrow-down"></b-icon>
                    </button>
                    <button
                      class="icon-btn edit"
                      :disabled="isProtected(item.id)"
                      @click="OpenEdit(item)"
                    >
                      <b-icon icon="pencil-square"></b-icon>
                    </button>
                    <button
                      class="icon-btn del"
                      :disabled="isProtected(item.id)"
                      @click="Trash(item)"
                    >
                      <b-icon icon="trash"></b-icon>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="3" class="empty-row">{{ $t("Ma'lumot topilmadi") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-footer">
          <div class="info">{{ $t("Jami") }}: <b>{{ items.length }}</b></div>
        </div>
      </div>
    </div>

    <!-- CREATE/EDIT MODAL -->
    <b-modal
      id="paytype-modal"
      :title="modalTitle"
      hide-footer
      centered
      @shown="onModalShown"
      @hidden="onModalHidden"
    >
      <div class="form-unit">
        <label>{{ $t("Nomi") }} <span class="required-star">*</span></label>
        <input
          ref="nameInput"
          v-model="form.name"
          type="text"
          :placeholder="$t('Masalan: Click, Payme')"
          :class="{ 'input-error': errorText }"
          @keydown.enter="Save()"
        >
        <small v-if="errorText" class="error-text">{{ errorText }}</small>
      </div>
      <div class="modal-footer-row">
        <button class="btn-cancel" type="button" @click="$bvModal.hide('paytype-modal')">
          {{ $t("Bekor qilish") }}
        </button>
        <button class="btn-save" type="button" :disabled="saving" @click="Save()">
          <b-icon icon="check-all" class="mr-1"></b-icon>
          {{ saving ? $t("Saqlanmoqda...") : $t("Saqlash") }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
// Tizim ichida ishlatiladigan to'lov turlari — o'zgartirilmaydi va o'chirilmaydi
// 1=Naqd, 2=Plastik, 3=Dollar, 4=Hisob
const PROTECTED_IDS = [1, 2, 3, 4];
const RESERVED_NAMES = ['naqd', 'plastik', 'plastic', 'dollar', 'hisob', 'нақд', 'наҳд', 'пластик', 'доллар', 'хисоб', 'шот', 'счёт'];

export default {
  data() {
    return {
      items: [],
      filter: "",
      form: { id: null, name: "" },
      editing: false,
      saving: false,
      errorText: "",
    };
  },
  computed: {
    filteredRows() {
      if (!this.filter || this.filter.length < 1) return this.items;
      const q = this.filter.toLowerCase();
      return this.items.filter(r => r.name && r.name.toLowerCase().includes(q));
    },
    modalTitle() {
      return this.editing ? this.$t("To'lov turini tahrirlash") : this.$t("Yangi to'lov turi");
    }
  },
  created() {
    this.Load();
  },
  methods: {
    isProtected(id) {
      return PROTECTED_IDS.indexOf(id) !== -1;
    },
    // item ning items massividagi haqiqiy pozitsiyasi (saralash uchun)
    itemPos(item) {
      return this.items.findIndex(i => i.id === item.id);
    },
    // Yuqoriga (-1) yoki pastga (+1) ko'chirish va serverga saqlash
    move(item, dir) {
      if (this.filter) return; // saralash faqat qidiruvsiz holatda
      const idx = this.itemPos(item);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= this.items.length) return;
      const arr = this.items.slice();
      const tmp = arr[idx];
      arr[idx] = arr[target];
      arr[target] = tmp;
      this.items = arr; // Vue 2 reaktivligi uchun butun massivni almashtiramiz
      this.persistOrder();
    },
    persistOrder() {
      let self = this;
      const ids = this.items.map(i => i.id);
      axios.patch("api/v1/pay-type/reorder", { ids }).catch(function(error) {
        self.$store.state.errors = (error.response && error.response.data && (error.response.data.message || error.response.data))
          || self.$t("Tartiblashda xatolik");
        self.Load(); // xato bo'lsa serverdagi tartibni qaytaramiz
      });
    },
    Load() {
      let self = this;
      axios.get("api/v1/pay-type").then(function(response) {
        self.items = response.data || [];
      }).catch(function(error) {
        self.$store.state.errors = (error.response && error.response.data && error.response.data.message)
          || self.$t("Yuklashda xatolik");
      });
    },
    OpenCreate() {
      this.editing = false;
      this.form = { id: null, name: "" };
      this.errorText = "";
      this.$bvModal.show("paytype-modal");
    },
    OpenEdit(item) {
      if (this.isProtected(item.id)) {
        this.$store.state.errors = this.$t("Tizim to'lov turi tahrirlanmaydi");
        return;
      }
      this.editing = true;
      this.form = { id: item.id, name: item.name };
      this.errorText = "";
      this.$bvModal.show("paytype-modal");
    },
    Validate() {
      const name = (this.form.name || "").trim();
      if (name.length < 3) {
        this.errorText = this.$t("Nomi kamida 3 ta belgi bo'lishi kerak");
        return false;
      }
      if (name.length > 20) {
        this.errorText = this.$t("Nomi 20 ta belgidan oshmasligi kerak");
        return false;
      }
      // Tizim nomlari band — Dollar, Naqd va boshq. boshqa nom kiritish kerak
      if (RESERVED_NAMES.indexOf(name.toLowerCase()) !== -1) {
        this.errorText = this.$t("Bu nom tizimda band, boshqa nom kiriting");
        return false;
      }
      const dup = this.items.find(i => i.name.toLowerCase() === name.toLowerCase() && i.id !== this.form.id);
      if (dup) {
        this.errorText = this.$t("Bunday nom allaqachon mavjud");
        return false;
      }
      this.errorText = "";
      return true;
    },
    Save() {
      if (!this.Validate()) return;
      let self = this;
      self.saving = true;
      const url = self.editing
        ? "api/v1/pay-type/id/" + self.form.id
        : "api/v1/pay-type";
      const method = self.editing ? "patch" : "post";
      axios({
        method, url,
        data: { name: self.form.name.trim() }
      }).then(function() {
        self.saving = false;
        self.$bvModal.hide("paytype-modal");
        self.Load();
      }).catch(function(error) {
        self.saving = false;
        self.errorText = (error.response && error.response.data && (error.response.data.message || error.response.data))
          || self.$t("Saqlashda xatolik");
      });
    },
    Trash(item) {
      if (this.isProtected(item.id)) return;
      const ok = window.confirm(this.$t("O'chirilsinmi?") + " " + item.name);
      if (!ok) return;
      let self = this;
      axios({
        method: "delete",
        url: "api/v1/pay-type/id/" + item.id
      }).then(function() {
        self.Load();
      }).catch(function(error) {
        self.$store.state.errors = (error.response && error.response.data && (error.response.data.message || error.response.data))
          || self.$t("O'chirishda xatolik");
      });
    },
    onModalShown() {
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus();
      });
    },
    onModalHidden() {
      this.form = { id: null, name: "" };
      this.editing = false;
      this.errorText = "";
    }
  }
};
</script>

<style scoped>
.g-page {
  padding: 20px;
  background: #f8fafc;
  height: calc(100vh - 56px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.content-container {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  color: #a142f4;
  font-weight: 800;
  font-size: 26px;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
}

.modern-search {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 8px 15px 8px 35px;
  border-radius: 12px;
  width: 250px;
  outline: none;
  transition: 0.3s;
}

.modern-search:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 4px rgba(161, 66, 244, 0.1);
}

.add-btn {
  background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(161, 66, 244, 0.3);
}

.refresh-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.refresh-btn:hover {
  border-color: #a142f4;
  color: #a142f4;
}

.table-card {
  background: white;
  border-radius: 24px;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-wrapper {
  overflow-y: auto;
  flex-grow: 1;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.custom-table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 5;
  padding: 18px 15px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-bottom: 1px solid #f1f5f9;
}

.custom-table td {
  padding: 15px;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-size: 14px;
  color: #334155;
}

.custom-table tbody tr:hover {
  background: #faf8ff;
  cursor: pointer;
}

.id-badge {
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.font-bold { font-weight: 700; }

.protected-badge {
  background: #fef3c7;
  color: #b45309;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  cursor: pointer;
}

.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.move { background: #f1f5f9; color: #64748b; }
.move:hover:not(:disabled) { background: #a142f4; color: white; transform: scale(1.1); }

.edit { background: #eff6ff; color: #2563eb; }
.edit:hover:not(:disabled) { background: #2563eb; color: white; transform: scale(1.1); }

.del { background: #fff1f2; color: #e11d48; }
.del:hover:not(:disabled) { background: #e11d48; color: white; transform: scale(1.1); }

.table-footer {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f1f5f9;
  color: #64748b;
  font-size: 13px;
}

.empty-row {
  text-align: center;
  padding: 60px !important;
  color: #94a3b8;
  font-style: italic;
}

/* MODAL FORM */
.form-unit { margin-bottom: 20px; }
.form-unit label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #95a5a6;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-unit input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #f8fafc;
  border-radius: 12px;
  padding: 12px 15px;
  color: #2c3e50;
  font-weight: 600;
  transition: 0.3s;
  outline: none;
}

.form-unit input:focus {
  background: white;
  border-color: #a142f4;
  box-shadow: 0 0 0 4px rgba(161, 66, 244, 0.05);
}

.form-unit input.input-error {
  background: #fff1f2;
  border-color: #e11d48;
}

.required-star { color: #e11d48; font-weight: 800; margin-left: 2px; }
.error-text {
  display: block;
  color: #e11d48;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
  text-transform: none;
  letter-spacing: 0;
}

.modal-footer-row {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.btn-save {
  background: #a142f4;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s;
}

.btn-save:hover:not(:disabled) { background: #8e39d9; }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-cancel {
  background: white;
  border: 1px solid #dcdde1;
  color: #7f8c8d;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
}
</style>
